import { DownloadOutlined, UploadOutlined } from "@ant-design/icons";
import { Button, Card, Col, Row, Space, Switch, Table } from "antd";
import { SorterResult } from "antd/lib/table/interface";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useState } from "react";

import { BonusTemplateAPI } from "@/api";
import { useExportHandler, useImportHandler } from "@/hooks";
import Query from "@/models/query";
import MoneyFormatter from "@/ui/_common_/money-formatter";

function BonusTemplates() {
  const [orderBy, setOrderBy] = useState<["id", "asc" | "desc"]>(["id", "asc"]);

  const [getAllQuery] = useState(() => {
    const query = new Query(BonusTemplateAPI.getAll);
    query.submit({});
    return query;
  });

  const [importQuery] = useState(() => new Query(BonusTemplateAPI.import));
  const [exportQuery] = useState(() => new Query(BonusTemplateAPI.export));

  const handleImportClick = useImportHandler({ getAllQuery, importQuery });

  const handleExportClick = useExportHandler({
    exportQuery,
    fileName: "bonus-templates.csv",
  });

  useEffect(() => {
    getAllQuery.submit({ orderBy });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy]);

  const handleTableChange = useCallback(
    (
      pagination: any,
      filters: any,
      sorter: SorterResult<any> | SorterResult<any>[],
    ) => {
      if (
        !Array.isArray(sorter) &&
        typeof sorter.columnKey === "string" &&
        typeof sorter.order === "string" &&
        sorter.columnKey === "id"
      ) {
        setOrderBy([
          sorter.columnKey,
          sorter.order === "ascend" ? "asc" : "desc",
        ]);
      }
    },
    [],
  );

  return (
    <Space direction="vertical">
      <Card size="small">
        <Row gutter={[12, 12]} wrap={true}>
          <Col>
            <Button
              icon={<DownloadOutlined />}
              loading={importQuery.isPending}
              onClick={handleImportClick}
            >
              {"Import"}
            </Button>
          </Col>
          <Col>
            <Button
              icon={<UploadOutlined />}
              loading={exportQuery.isPending}
              onClick={handleExportClick}
            >
              {"Export"}
            </Button>
          </Col>
        </Row>
      </Card>
      <Card size="small">
        <Table
          loading={getAllQuery.isPending}
          dataSource={getAllQuery.isFulfilled ? getAllQuery.data.data : []}
          pagination={false}
          size="small"
          rowKey="id"
          onChange={handleTableChange}
        >
          <Table.Column
            title={"ID"}
            key="id"
            dataIndex="id"
            sorter={true}
            defaultSortOrder={orderBy[1] === "asc" ? "ascend" : "descend"}
            sortDirections={["ascend", "descend", "ascend"]}
          />
          <Table.Column title={"Name"} dataIndex="name" width="100%" />
          <Table.Column title={"Type"} dataIndex="type" />
          <Table.Column
            title={"Min. deposit"}
            dataIndex="minDeposit"
            align="right"
            render={(minDeposit: number) => (
              <MoneyFormatter cents={minDeposit} />
            )}
          />
          <Table.Column
            title={"Max. deposit"}
            dataIndex="maxDeposit"
            align="right"
            render={(maxDeposit: number) => (
              <MoneyFormatter cents={maxDeposit} />
            )}
          />
          <Table.Column
            title={"Percentage of deposit"}
            dataIndex="depositPercentage"
            align="right"
          />
          <Table.Column
            title={"Max. activations"}
            dataIndex="maxActivationsCount"
            align="right"
          />
          <Table.Column
            title={"Player balance threshold"}
            dataIndex="playerBalanceThreshold"
            align="right"
            render={(playerBalanceThreshold: number) => (
              <MoneyFormatter cents={playerBalanceThreshold} />
            )}
          />
          <Table.Column title={"Cooldown"} dataIndex="cooldown" align="right" />
          <Table.Column
            title={"Wager enabled"}
            dataIndex="isWagerEnabled"
            align="right"
            render={(isWagerEnabled: boolean) => (
              <Switch checked={isWagerEnabled} />
            )}
          />
        </Table>
      </Card>
    </Space>
  );
}

export default observer(BonusTemplates);
